import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useRef } from 'react';

import { useHttpClient } from '../../../lib/http-client';

const NAMESPACE = 'announcements';
const QUERY_KEY = ['userContentDismissals', NAMESPACE];

interface UserContentDismissal {
	id: string;
	name: string;
	namespace: string;
	dismissed_at: string;
}

interface UserContentDismissalResponse {
	result: UserContentDismissal[];
	success: boolean;
}

/**
 * Hook to manage announcement dismissals (read status).
 * Uses the UserContentDismissal API with namespace 'announcements'
 * and name format 'announcement-{announcementId}'.
 */
export const useAnnouncementDismissals = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();
	// Track in-flight dismissals to prevent duplicate API calls
	const pendingDismissalsRef = useRef(new Set<string>());

	const { data, isLoading } = useQuery(
		QUERY_KEY,
		() =>
			http.get<UserContentDismissalResponse>('/user_content_dismissals', {
				params: {
					namespace: NAMESPACE,
				},
			}),
		{
			select: (response) => response.data.result || [],
			refetchInterval: Infinity,
		}
	);

	// Extract announcement IDs from dismissal names (format: 'announcement-{id}')
	const dismissedAnnouncementIds = useMemo(() => {
		if (!data) return new Set<string>();
		return new Set(
			data
				.map((dismissal) => {
					const match = dismissal.name.match(/^announcement-(.+)$/);
					return match ? match[1] : null;
				})
				.filter((id): id is string => id !== null)
		);
	}, [data]);

	const dismissMutation = useMutation<void, Error, string>(
		async (announcementId: string) => {
			await http.post('/user_content_dismissals', {
				user_content_dismissal: {
					name: `announcement-${announcementId}`,
					namespace: NAMESPACE,
				},
			});
		},
		{
			onSuccess: (_data, announcementId) => {
				pendingDismissalsRef.current.delete(announcementId);
				queryClient.invalidateQueries(QUERY_KEY);
			},
			onError: (_error, announcementId) => {
				pendingDismissalsRef.current.delete(announcementId);
			},
		}
	);

	const dismissAnnouncement = useCallback(
		(announcementId: string) => {
			// Only dismiss if not already dismissed and not currently being dismissed
			if (!dismissedAnnouncementIds.has(announcementId) && !pendingDismissalsRef.current.has(announcementId)) {
				pendingDismissalsRef.current.add(announcementId);
				dismissMutation.mutate(announcementId);
			}
		},
		[dismissedAnnouncementIds, dismissMutation]
	);

	const isAnnouncementDismissed = useCallback((announcementId: string) => dismissedAnnouncementIds.has(announcementId), [dismissedAnnouncementIds]);

	return {
		isLoading,
		dismissedAnnouncementIds,
		dismissAnnouncement,
		isAnnouncementDismissed,
	};
};
